import React, { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, CreditCard, FileText, PlayCircle, ShoppingBag, Sparkles } from 'lucide-react';

import { loadStorefrontLive } from './api';
import { Empty } from './parts';
import { countryName } from './countries';
import { useLiveResource } from './useLive';
import { Flag, NUMBER, Panel, PulseMap, ago, lengthOf, useNow } from './MobileParts';
import {
  ChannelMark, DeviceMark, EventIcon, PathText, RankRows, ReadState, StorefrontGate, StorefrontHead, eventLabel, eventTone,
  inWords, money, st, stCount, useStoreNames, useStorefrontSite,
} from './StorefrontParts';

// Who is on the store right now: where they are, what they are looking at,
// what is in their bags and who is paying. Read again every ten seconds.

const POLL_MS = 10000;

export function sessionLink(id) {
  return `/xomics/store/sessions?session=${encodeURIComponent(id)}`;
}

export function placeName(row) {
  return row.city || (row.country ? countryName(row.country) : st('place_unknown'));
}

// Where a live visit has got to: bought, paying, a bag, or only looking.
function StateChip({ session }) {
  if (session.purchased) return <span className="xo-sf-state is-bought"><Sparkles aria-hidden="true" />{st('live.state_bought')}</span>;
  if (session.checkout) return <span className="xo-sf-state is-checkout"><CreditCard aria-hidden="true" />{st('live.state_checkout', { step: session.checkout.step })}</span>;
  if (session.bag && session.bag.items > 0) {
    return (
      <span className="xo-sf-state is-bag"><ShoppingBag aria-hidden="true" />
        {session.bag.valueMinor !== null && session.bag.currency ? money(session.bag.valueMinor, session.bag.currency) : stCount('items_count', session.bag.items)}
      </span>
    );
  }
  return null;
}

function OnTheStore({ data, now }) {
  const names = useStoreNames();
  const on = data.visitors > 0;
  const totals = data.totals;
  return (
    <section className="xo-mob-now xo-sf-now">
      <header>
        <span className={`xo-mob-now-dot${on ? ' is-on' : ''}`} aria-hidden="true" />
        <span className="xo-mob-now-title"><strong>{st('live.on_store')}</strong></span>
        <strong className="xo-mob-now-count">{NUMBER.format(data.visitors)}</strong>
      </header>
      <dl className="xo-sf-now-states">
        <div><dt>{st('live.browsing')}</dt><dd>{NUMBER.format(totals.browsing)}</dd></div>
        <div><dt>{st('live.with_bag')}</dt><dd>{NUMBER.format(totals.withBag)}</dd></div>
        <div><dt>{st('live.in_checkout')}</dt><dd>{NUMBER.format(totals.inCheckout)}</dd></div>
        <div><dt>{st('live.bought')}</dt><dd>{NUMBER.format(totals.purchased)}</dd></div>
      </dl>
      {data.sessions.length === 0 ? <p className="xo-mob-now-empty">{st('live.nobody')}</p> : (
        <ol className="xo-mob-now-list xo-sf-now-list">
          {data.sessions.map((session) => (
            <li key={session.id}>
              <Link to={sessionLink(session.id)}>
                <span className="xo-sf-now-place">{session.country ? <Flag code={session.country} /> : <DeviceMark device={session.deviceType} />}</span>
                <span className="xo-mob-now-who">
                  <strong>{placeName(session)}</strong>
                  <small title={session.path || '/'}><DeviceMark device={session.deviceType} />{names.pageOrPath(session.path)}</small>
                </span>
                <span className="xo-sf-now-side">
                  <StateChip session={session} />
                  <span className="xo-mob-now-when">{lengthOf(Math.max(0, now - session.startedAt))}</span>
                </span>
                <ChevronRight aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

// What an event was about, as a visitor would say it: the product's name, what
// was clicked (never a translation key), and the page's title.
function feedLine(event, names) {
  if (event.productCode) return names.product(event.productCode);
  const subject = event.subject && !event.subject.includes('.') ? inWords(event.subject) : null;
  return [subject, event.path ? names.pageOrPath(event.path) : null].filter(Boolean).join(' · ');
}

function Feed({ feed, now }) {
  const names = useStoreNames();
  if (feed.length === 0) return <Empty title={st('live.feed_empty')} />;
  return (
    <ol className="xo-sf-feed">
      {feed.map((event, index) => (
        <li key={`${event.sessionId}-${event.at}-${index}`} className={`is-${eventTone(event.type)}`}>
          <Link to={sessionLink(event.sessionId)}>
            <span className="xo-sf-feed-icon"><EventIcon type={event.type} /></span>
            <span className="xo-sf-feed-main">
              <strong>{eventLabel(event.type)}</strong>
              <small>{feedLine(event, names)}</small>
            </span>
            <span className="xo-sf-feed-when">{ago(event.at, now)}</span>
          </Link>
        </li>
      ))}
    </ol>
  );
}

// The live visits by channel and by device, counted from the list above.
function Mix({ sessions }) {
  const channels = useMemo(() => {
    const counts = new Map();
    for (const session of sessions) counts.set(session.channel, (counts.get(session.channel) || 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [sessions]);
  const devices = useMemo(() => {
    const counts = new Map();
    for (const session of sessions) counts.set(session.deviceType, (counts.get(session.deviceType) || 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [sessions]);
  if (sessions.length === 0) return <Empty title={st('live.nobody')} />;
  return (
    <div className="xo-sf-mix">
      <div>
        <h3 className="xo-mob-subhead">{st('live.channels')}</h3>
        <ul className="xo-sf-chips">
          {channels.map(([channel, number]) => <li key={channel}><ChannelMark channel={channel} /><b>{NUMBER.format(number)}</b></li>)}
        </ul>
      </div>
      <div>
        <h3 className="xo-mob-subhead">{st('live.devices')}</h3>
        <ul className="xo-sf-chips">
          {devices.map(([device, number]) => <li key={device || 'unknown'}><DeviceMark device={device} withLabel /><b>{NUMBER.format(number)}</b></li>)}
        </ul>
      </div>
    </div>
  );
}

function LiveView({ site, store }) {
  const load = useCallback(() => loadStorefrontLive(site.code), [site.code]);
  const live = useLiveResource(['store_live', 'store'], load, { reconcileMs: POLL_MS });
  const now = useNow(5000);
  const head = (
    <StorefrontHead title={st('live.title')} subtitle={st('live.subtitle', { site: site.name })} store={store} />
  );
  if (live.data === null) return <div className="xo-platform-page xo-mobile-page xo-sf-page">{head}<ReadState resource={live} label={st('live.loading')} /></div>;
  const data = live.data;
  const pings = data.places.map((place) => ({
    city: place.city || place.region || countryName(place.country), country: place.country, lat: place.lat, lng: place.lng,
    at: data.generatedAt, count: place.visitors,
  }));
  const countries = [...data.places.reduce((map, place) => {
    if (place.country) map.set(place.country, (map.get(place.country) || 0) + place.visitors);
    return map;
  }, new Map()).entries()].map(([code, visitors]) => ({ code, visitors }));
  return (
    <div className="xo-platform-page xo-mobile-page xo-sf-page">
      {head}
      {live.error && <p className="xo-alert t-warn" role="alert">{live.error}</p>}
      <div className="xo-mob-hero">
        <Panel title={st('live.map_title')} detail={st('live.map_detail', { minutes: Math.round(data.windowMs / 60000) })} className="xo-mob-hero-map">
          <PulseMap countries={countries} pings={pings} valueKey="visitors" valueLabel={st('kpi.visitors')}
            pingCountKey="ui.storefront.visitors_count" height={420} label={st('live.map_label')} />
        </Panel>
        {/* Who is on the store, as tall as the map beside it. */}
        <div className="xo-mob-hero-side xo-sf-live-side">
          <OnTheStore data={data} now={now} />
        </div>
      </div>
      {/* The pages open and who the visitors are, stacked beside what is happening. */}
      <div className="xo-mob-row is-halves is-even">
        <div className="xo-sf-stack">
          <Panel title={st('live.pages_title')} detail={st('live.pages_detail')}>
            <RankRows empty={<Empty title={st('live.nobody')} />} rows={data.pages.map((page) => ({
              key: page.path, icon: <FileText aria-hidden="true" />, label: <PathText path={page.path} />, title: page.path,
              value: page.visitors, display: stCount('visitors_count', page.visitors),
            }))} />
          </Panel>
          <Panel title={st('live.mix_title')} detail={st('live.mix_detail')}
            aside={<Link className="xo-mob-link" to="/xomics/store/sessions"><PlayCircle aria-hidden="true" />{st('live.all_sessions')}</Link>}>
            <Mix sessions={data.sessions} />
          </Panel>
        </div>
        <Panel title={st('live.feed_title')} detail={st('live.feed_detail')}>
          <Feed feed={data.feed} now={now} />
        </Panel>
      </div>
    </div>
  );
}

export default function StorefrontLive() {
  const store = useStorefrontSite();
  return <StorefrontGate store={store}>{(site) => <LiveView site={site} store={store} />}</StorefrontGate>;
}
